import * as React from "react"
import {useState} from "react"
import {LucideIcon} from "lucide-react"
import {Toggle} from "@/components/ui/toggle"
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {MetaHandler} from "@/main/common/MetaHandler"
import {LocalizationUtil} from "@/main/util/LocalizationUtil"
import {
    type ContextualAccountAccessDialogContext,
    resolveContextualAccountAccessDialogOptions,
} from "@/main/react/account_access/AccountAccessDialogContext"
import {openAccountAccessDialog} from "@/main/react/account_access/AccountAccessDisplayerDialog"
import {CtaButtonContent} from "./CtaButtonContent"
import {
    CtaCountFeedback,
    CtaInteractionEffect,
    CtaSize,
    CtaVariant,
    getBaseToggleVariant,
    getCtaClasses,
    getCtaIconBaseClass,
    resolveActiveClass,
    warnIfCtaHasNoAccessibleName,
} from "./ctaShared"
import {useCtaInteractionFeedback} from "./useCtaInteractionFeedback"

type CtaToggleBaseProps = {
    icon: LucideIcon
    text?: string | null
    /** Accessible name for an icon-only toggle. Same contract as `CtaButton.ariaLabel`. */
    ariaLabel?: string | null
    buttonCount?: number | null
    disabled?: boolean
    extraClass?: string
    buttonActive?: boolean
    pressed?: boolean
    activeClass?: string
    buttonData?: string | null
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    onPressedChange?: (pressed: boolean) => void | Promise<void>
    roundedLeft?: boolean
    roundedRight?: boolean
    displayTextOnMobile?: boolean
    requiresLogin?: boolean
    disabledReason?: string | null
    loadingText?: string | null
    loadingReason?: string | null
    size?: CtaSize
    variant?: CtaVariant
    playwrightId?: string
    suppressTooltip?: boolean
    tooltip?: string | null
    isLoading?: boolean
    interactionEffect?: CtaInteractionEffect
    countFeedback?: CtaCountFeedback
}

type CtaToggleLoginGateProps =
    | {
        requiresLogin?: false
        accountAccessDialog?: never
    }
    | {
        requiresLogin: true
        accountAccessDialog: ContextualAccountAccessDialogContext
    }

export type CtaToggleProps = CtaToggleBaseProps & CtaToggleLoginGateProps

export type CtaToggleComponentProps = CtaToggleProps & Omit<React.ComponentProps<typeof Toggle>, "size" | "variant" | "pressed" | "defaultPressed" | "onPressedChange" | "onClick" | "children">

export const CtaToggle = React.forwardRef<HTMLButtonElement, CtaToggleComponentProps>(function CtaToggle(
    {
        icon: Icon,
        text = null,
        ariaLabel = null,
        buttonCount = null,
        disabled = false,
        extraClass = "",
        buttonActive = false,
        pressed,
        activeClass,
        buttonData = null,
        onClick,
        onPressedChange,
        roundedLeft = false,
        roundedRight = false,
        displayTextOnMobile = true,
        title,
        requiresLogin = false,
        accountAccessDialog,
        disabledReason = null,
        loadingText = null,
        loadingReason = null,
        size = "md",
        variant = "ghost",
        playwrightId = undefined,
        suppressTooltip = false,
        tooltip = null,
        isLoading: externalIsLoading,
        interactionEffect,
        countFeedback = "none",
        ...rest
    },
    ref,
) {
    const [internalIsLoading, setInternalIsLoading] = useState(false)

    const effectiveIsLoading = typeof externalIsLoading === "boolean" ? externalIsLoading : internalIsLoading
    const effectivePressed = typeof pressed === "boolean" ? pressed : buttonActive
    const resolvedActiveClass = resolveActiveClass(effectivePressed, variant, activeClass, extraClass)
    const classes = getCtaClasses({
        size,
        variant,
        roundedLeft,
        roundedRight,
        resolvedActiveClass,
        extraClass,
    })
    const effectiveDisabled = disabled || effectiveIsLoading
    const effectiveTooltip = disabledReason ?? (effectiveIsLoading ? (loadingReason ?? undefined) : (tooltip ?? undefined))
    const effectiveText = effectiveIsLoading ? (loadingText ?? text) : text
    const restAriaProps = rest as Record<string, unknown>
    const effectiveAriaLabel = ariaLabel ?? (restAriaProps["aria-label"] as string | undefined) ?? undefined
    warnIfCtaHasNoAccessibleName("CtaToggle", {
        text: effectiveText,
        ariaLabel: effectiveAriaLabel,
        title,
        ariaLabelledBy: restAriaProps["aria-labelledby"],
    })
    const effectiveIconBase = getCtaIconBaseClass(size)
    const toggleSize = size === "md" ? "default" : size
    const effectiveInteractionEffect = interactionEffect ?? (variant === "link" ? "none" : "weight")
    const {rootInteractionProps, armInteraction} = useCtaInteractionFeedback({
        interactionEffect: effectiveInteractionEffect,
        countFeedback,
        active: effectivePressed,
        count: buttonCount,
    })

    const handlePressedChange = async (nextPressed: boolean) => {
        if (effectiveDisabled) return

        if (requiresLogin && !MetaHandler.isUserLoggedIn()) {
            openAccountAccessDialog(LocalizationUtil.get, resolveContextualAccountAccessDialogOptions(LocalizationUtil.get, accountAccessDialog!))
            return
        }

        if (!onPressedChange) return

        try {
            armInteraction()
            const result = onPressedChange(nextPressed)
            if (result && typeof (result as Promise<void>).then === "function") {
                if (typeof externalIsLoading !== "boolean") setInternalIsLoading(true)
                try {
                    await result
                } finally {
                    if (typeof externalIsLoading !== "boolean") setInternalIsLoading(false)
                }
            }
        } catch (err) {
            if (typeof externalIsLoading !== "boolean") setInternalIsLoading(false)
            throw err
        }
    }

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        if (effectiveDisabled) {
            try {
                e.preventDefault()
                e.stopPropagation()
            } catch {
                // ignore
            }
            return
        }

        if (requiresLogin && !MetaHandler.isUserLoggedIn()) {
            try {
                e.preventDefault()
                e.stopPropagation()
            } catch {
                // ignore
            }
            openAccountAccessDialog(LocalizationUtil.get, resolveContextualAccountAccessDialogOptions(LocalizationUtil.get, accountAccessDialog!))
            return
        }

        try {
            e.stopPropagation()
        } catch {
            // ignore
        }

        ;(e.currentTarget as HTMLButtonElement)?.blur()
        armInteraction()
        onClick?.(e)
    }

    const toggleElement = (
        <Toggle
            ref={ref}
            pressed={effectivePressed}
            disabled={effectiveDisabled}
            variant={getBaseToggleVariant(variant)}
            size={toggleSize as any}
            className={classes}
            onClick={handleClick}
            onPressedChange={(nextPressed) => {
                void handlePressedChange(nextPressed)
            }}
            aria-label={effectiveAriaLabel}
            title={title ?? (effectiveText != null ? String(effectiveText) : undefined)}
            data-playwright-id={playwrightId}
            data-variable={buttonData ?? undefined}
            {...rootInteractionProps}
            {...rest}
        >
            <CtaButtonContent
                icon={Icon}
                text={text}
                loadingText={loadingText}
                buttonCount={buttonCount}
                isLoading={effectiveIsLoading}
                displayTextOnMobile={displayTextOnMobile}
                resolvedActiveClass={resolvedActiveClass}
                iconBaseClass={effectiveIconBase}
            />
        </Toggle>
    )

    if (effectiveTooltip && !suppressTooltip) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-block">{toggleElement}</span>
                </TooltipTrigger>
                <TooltipContent side="top">{effectiveTooltip}</TooltipContent>
            </Tooltip>
        )
    }

    return toggleElement
})

CtaToggle.displayName = "CtaToggle"
